import React, { useEffect, useMemo, useState } from "react";
import "./SiteHub.css";
import { applyBrandHubSeo } from "./setBrandHubSeo";
import {
  bootAnalytics,
  trackPageView,
  trackBookClick,
  trackInnerVoiceClick,
  trackOrasisStagingClick,
} from "../utils/nbfAnalytics";

const BOOK_COVER = {
  he: "/books/book-he.png",
  en: "/books/book-en.png",
  es: "/books/book-es.png",
};

const IV_PREVIEW = {
  he: "/orasis/assets/innervoice-preview-he.png",
  en: "/orasis/assets/innervoice-preview-en.png",
  es: "/orasis/assets/innervoice-preview-es.png",
};

const ORASIS_THUMB = {
  he: "/orasis/assets/report-preview-thumb-he.png",
  en: "/orasis/assets/report-preview-thumb-en.png",
  es: "/orasis/assets/report-preview-thumb-es.png",
};

const ORASIS_BRIEF = [
  {
    id: "personal",
    price: "$69",
    title: { he: "דוח אישי", en: "Personal", es: "Personal" },
  },
  {
    id: "couple",
    price: "$99",
    title: { he: "דוח זוגי", en: "Couple", es: "Pareja" },
  },
  {
    id: "dream",
    price: "$39",
    title: { he: "דוח חלום", en: "Dream", es: "Sueño" },
  },
  {
    id: "pattern",
    price: "$25",
    title: { he: "דפוס / סיבה ותוצאה", en: "Pattern", es: "Patrón" },
  },
];

const HUB_COPY = {
  he: {
    htmlLang: "he",
    dir: "rtl",
    langGroupAria: "בחירת שפה",
    navAria: "ניווט ראשי",
    navHome: "בית",
    navBook: "הספר",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    navMenuOpen: "פתח תפריט",
    navMenuClose: "סגור תפריט",
    heroTitle: "מה שעיצב אותך אז עדיין בוחר במקומך.",
    heroBoom: "השינוי מתחיל ברגע שאתה מעז להביט לו בעיניים.",
    heroBody:
      "Not Broken Forever הוא מרחב להבנה, ריפוי ובהירות. דרך סיפור אמיתי, מסע אישי יומי ודוחות עומק שעוזרים לך להבין מה קורה בתוכך ומה נכון לעשות מכאן.",
    heroCtaFeel: "להתחיל מסע אישי עם InnerVoice",
    heroCtaQuestion: "לקבל דוח עומק על שאלה שמעסיקה אותי",
    heroCtaBook: "להכיר את הספר",
    recogTitle: "אם זה נשמע מוכר, אתה במקום הנכון.",
    recogBody:
      "יש ימים שבהם אותם דפוסים חוזרים. יש רגעים של משבר, קשר שמטלטל, חלום שלא עוזב, או שאלה שאין עליה תשובה ברורה. Not Broken Forever נבנה בדיוק סביב הרגעים האלה.",
    recogItems: [
      "מרגיש תקוע, בלי לדעת למה הבחירות חוזרות על עצמן",
      "חוזר לאותם דפוסים באהבה, בעבודה, או מול עצמך",
      "מתמודד עם משבר, פרידה, או קשר שמבקש בהירות",
      "נושא חלום או שאלה שאין לה תשובה פשוטה",
    ],
    brandTitle: "שלושה שערים. מסע אחד של הבנה.",
    brandBody:
      "Not Broken Forever נותן לך שלוש דרכים להבין את עצמך טוב יותר: דרך הסיפור והתובנות שבספר, דרך מסע אישי עם InnerVoice, או דרך דוח עומק של Orasis על שאלה שמעסיקה אותך עכשיו.",
    gatesLabel: "בחרו שער",

    bookLabel: "הספר",
    bookTitle: "לא שבור לנצח",
    bookPromise: "סיפור אמיתי שפותח דלת להכרה: מה שעברת לא מגדיר אותך לנצח.",
    bookPoints: [
      "אהבה, כאב, נטישה, והאפשרות לחזור לחיים",
      "שפה לכאב בלי להפוך אותו לזהות",
      "תחושה שמישהו כבר הלך בדרך דומה",
    ],
    bookImgAlt: "כריכת הספר לא שבור לנצח",
    bookCta: "להכיר את הספר",

    ivLabel: "InnerVoice",
    ivTitle: "מסע יומי של הקשבה",
    ivPromise: "מסע אישי שמתפתח איתך יום אחרי יום.",
    ivPoints: [
      "בכל יום InnerVoice מקשיב למה שאתה כותב",
      "מבין את ההקשר ומוביל אותך לשאלה הבאה שמתאימה למה שעובר עליך",
    ],
    ivImgAlt: "תצוגה של מסע InnerVoice",
    ivCta: "להתחיל מסע אישי עם InnerVoice",

    orasisLabel: "Orasis",
    orasisTitle: "דוח עומק לשאלה שמעסיקה אותך",
    orasisPromise:
      "יש לך שאלה שמעסיקה אותך עכשיו? Orasis מנתח אותה לעומק ומחזיר לך דוח אישי שעוזר לעשות סדר בתמונה ולראות אותה מזוויות שלא תמיד קל לראות לבד.",
    orasisPointsIntro: "ארבעה סוגי דוחות:",
    orasisImgAlt: "תצוגה מקדימה של מבנה דוח Orasis",
    orasisCta: "לקבל דוח עומק על שאלה שמעסיקה אותי",

    compareTitle: "מה מתאים לך עכשיו?",
    compareBody: "בחרו לפי מה שאתם צריכים היום. אין צורך להבין מראש מהו \"שער\".",
    compareBookWhen: "אם אתה רוצה להתחיל דרך סיפור, תובנות ותרגילים → הספר",
    compareIvWhen: "אם אתה רוצה תהליך אישי לאורך זמן → InnerVoice",
    compareOrasisWhen: "אם יש שאלה אחת שמעסיקה אותך עכשיו → Orasis",

    authorEyebrow: "למה המערכת הזו נוצרה",
    authorName: "Phineas J. Cohen",
    authorBody:
      "המערכת נולדה מתוך סיפור אישי של כאב, אובדן, וחיפוש אחרי דרך חזרה. הספר נותן את הסיפור וההכרה. InnerVoice הופך הבנה לתהליך יומי. Orasis נותן דוח עומק לשאלה שמעסיקה אותך עכשיו. שלוש דרכים. בית אחד.",
    authorImgAlt: "Phineas J. Cohen",

    closeTitle: "מאיפה נכון לך להתחיל עכשיו?",
    closeBody:
      "אם אתה רוצה להבין מה עובר עליך לאורך זמן, התחל במסע. אם יש שאלה שלוחצת עכשיו, לך לדוח. אם אתה צריך קודם להרגיש שמישהו כבר עבר משהו דומה, התחל בספר.",
    closeCtaFeel: "להתחיל מסע אישי עם InnerVoice",
    closeCtaQuestion: "לקבל דוח עומק על שאלה שמעסיקה אותי",
    closeCtaBook: "להכיר את הספר",

    footerBook: "הספר",
    footerApp: "InnerVoice",
    footerOrasis: "Orasis",
    footerPrivacy: "פרטיות",
    footerTerms: "תנאים",
    footerContact: "יצירת קשר",
    langHe: "עברית",
    langEn: "English",
    langEs: "Español",
  },
  en: {
    htmlLang: "en",
    dir: "ltr",
    langGroupAria: "Language",
    navAria: "Main navigation",
    navHome: "Home",
    navBook: "Book",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    navMenuOpen: "Open menu",
    navMenuClose: "Close menu",
    heroTitle: "What shaped you then may still be making choices for you today.",
    heroBoom: "Change begins the moment you dare to face it.",
    heroBody:
      "Not Broken Forever is a space for understanding, healing, and clarity. Through a true story, a daily personal journey, and depth reports that help you see what is really happening within you, and what is wise to do from here.",
    heroCtaFeel: "Start a personal journey with InnerVoice",
    heroCtaQuestion: "Get an in-depth report on a question that matters to me",
    heroCtaBook: "Discover the book",
    recogTitle: "If this feels familiar, you are in the right place.",
    recogBody:
      "Some days the same patterns return. Some moments bring crisis, a relationship that shakes you, a dream that stays, or a question with no clean answer. Not Broken Forever was built for those moments.",
    recogItems: [
      "Feeling stuck, without knowing why the same choices keep returning",
      "Repeating patterns in love, work, or with yourself",
      "Facing a crisis, a breakup, or a bond that needs clarity",
      "Carrying a dream or a question that has no simple answer",
    ],
    brandTitle: "Three gates. One path of understanding.",
    brandBody:
      "Not Broken Forever gives you three ways to understand yourself better: through the story and insights in the book, through a personal journey with InnerVoice, or through an in-depth Orasis report about a question that matters to you right now.",
    gatesLabel: "Choose a gate",

    bookLabel: "The book",
    bookTitle: "Not Broken Forever",
    bookPromise: "A true story that opens a door of recognition: what you lived through does not define you forever.",
    bookPoints: [
      "Love, pain, abandonment, and the chance to begin again",
      "Language for pain without turning it into identity",
      "The feeling that someone has walked a path like yours",
    ],
    bookImgAlt: "Not Broken Forever book cover",
    bookCta: "Discover the book",

    ivLabel: "InnerVoice",
    ivTitle: "A daily journey of listening",
    ivPromise: "A personal journey that develops with you, day after day.",
    ivPoints: [
      "Each day InnerVoice listens to what you write",
      "Understands your context and leads you to the next question that fits what you are going through",
    ],
    ivImgAlt: "Preview of an InnerVoice journey",
    ivCta: "Start a personal journey with InnerVoice",

    orasisLabel: "Orasis",
    orasisTitle: "An in-depth report for the question on your mind",
    orasisPromise:
      "Do you have a question on your mind right now? Orasis analyzes it in depth and returns a personal report that helps you make sense of the picture and see it from angles that are not always easy to see alone.",
    orasisPointsIntro: "Four report types:",
    orasisImgAlt: "Preview of an Orasis report structure",
    orasisCta: "Get an in-depth report on a question that matters to me",

    compareTitle: "What fits you now?",
    compareBody: "Choose based on what you need today. You do not need to understand what a \"gate\" means first.",
    compareBookWhen: "If you want to begin through story, insights, and exercises → the book",
    compareIvWhen: "If you want a personal process over time → InnerVoice",
    compareOrasisWhen: "If one question is pressing on you right now → Orasis",

    authorEyebrow: "Why this system exists",
    authorName: "Phineas J. Cohen",
    authorBody:
      "This system was born from a personal story of pain, loss, and the search for a way back. The book offers the story and recognition. InnerVoice turns understanding into a daily process. Orasis delivers an in-depth report for the question on your mind now. Three paths. One home.",
    authorImgAlt: "Phineas J. Cohen",

    closeTitle: "Where is it right for you to begin now?",
    closeBody:
      "If you want to understand what you are going through over time, begin with the journey. If a question is pressing now, go toward the report. If you first need to feel that someone has walked through something like this, begin with the book.",
    closeCtaFeel: "Start a personal journey with InnerVoice",
    closeCtaQuestion: "Get an in-depth report on a question that matters to me",
    closeCtaBook: "Discover the book",

    footerBook: "Book",
    footerApp: "InnerVoice",
    footerOrasis: "Orasis",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerContact: "Contact",
    langHe: "עברית",
    langEn: "English",
    langEs: "Español",
  },
  es: {
    htmlLang: "es",
    dir: "ltr",
    langGroupAria: "Idioma",
    navAria: "Navegación principal",
    navHome: "Inicio",
    navBook: "Libro",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    navMenuOpen: "Abrir menú",
    navMenuClose: "Cerrar menú",
    heroTitle: "Lo que te marcó entonces todavía puede estar decidiendo por ti.",
    heroBoom: "El cambio comienza cuando te atreves a mirarlo de frente.",
    heroBody:
      "Not Broken Forever es un espacio de comprensión, sanación y claridad. A través de una historia real, un viaje personal diario e informes de profundidad que te ayudan a ver qué ocurre de verdad dentro de ti, y qué conviene hacer desde aquí.",
    heroCtaFeel: "Empezar un viaje personal con InnerVoice",
    heroCtaQuestion: "Recibir un informe en profundidad sobre una pregunta que me importa",
    heroCtaBook: "Conocer el libro",
    recogTitle: "Si esto te suena conocido, estás en el lugar correcto.",
    recogBody:
      "Hay días en que vuelven los mismos patrones. Hay momentos de crisis, un vínculo que te sacude, un sueño que no se va, o una pregunta sin respuesta limpia. Not Broken Forever nació justo para esos momentos.",
    recogItems: [
      "Sentirte atrapado, sin saber por qué se repiten las mismas decisiones",
      "Volver a los mismos patrones en el amor, el trabajo, o contigo",
      "Enfrentar una crisis, una ruptura, o un vínculo que pide claridad",
      "Cargar un sueño o una pregunta que no tiene respuesta sencilla",
    ],
    brandTitle: "Tres entradas. Un mismo camino de comprensión.",
    brandBody:
      "Not Broken Forever te ofrece tres formas de entenderte mejor: a través de la historia y las ideas del libro, con un viaje personal con InnerVoice, o con un informe en profundidad de Orasis sobre una pregunta que te importa ahora.",
    gatesLabel: "Elige una entrada",

    bookLabel: "El libro",
    bookTitle: "No roto para siempre",
    bookPromise: "Una historia real que abre una puerta de reconocimiento: lo que viviste no te define para siempre.",
    bookPoints: [
      "Amor, dolor, abandono, y la posibilidad de volver a empezar",
      "Lenguaje para el dolor sin convertirlo en identidad",
      "La sensación de que alguien ya caminó un camino parecido",
    ],
    bookImgAlt: "Portada del libro No roto para siempre",
    bookCta: "Conocer el libro",

    ivLabel: "InnerVoice",
    ivTitle: "Un viaje diario de escucha",
    ivPromise: "Un viaje personal que se desarrolla contigo, día tras día.",
    ivPoints: [
      "Cada día InnerVoice escucha lo que escribes",
      "Entiende tu contexto y te lleva a la siguiente pregunta que encaja con lo que te pasa",
    ],
    ivImgAlt: "Vista previa de un viaje InnerVoice",
    ivCta: "Empezar un viaje personal con InnerVoice",

    orasisLabel: "Orasis",
    orasisTitle: "Un informe en profundidad para la pregunta que te ocupa",
    orasisPromise:
      "¿Tienes una pregunta que te ocupa ahora? Orasis la analiza en profundidad y te devuelve un informe personal que ayuda a ordenar la imagen y verla desde ángulos que no siempre es fácil ver solo.",
    orasisPointsIntro: "Cuatro tipos de informe:",
    orasisImgAlt: "Vista previa de la estructura de un informe Orasis",
    orasisCta: "Recibir un informe en profundidad sobre una pregunta que me importa",

    compareTitle: "¿Qué te conviene ahora?",
    compareBody: "Elige según lo que necesitas hoy. No hace falta entender antes qué es una \"entrada\".",
    compareBookWhen: "Si quieres empezar por historia, ideas y ejercicios → el libro",
    compareIvWhen: "Si quieres un proceso personal a lo largo del tiempo → InnerVoice",
    compareOrasisWhen: "Si hay una pregunta que te ocupa ahora → Orasis",

    authorEyebrow: "Por qué existe este sistema",
    authorName: "Phineas J. Cohen",
    authorBody:
      "Este sistema nació de una historia personal de dolor, pérdida, y la búsqueda de un camino de vuelta. El libro ofrece la historia y el reconocimiento. InnerVoice convierte la comprensión en un proceso diario. Orasis entrega un informe en profundidad para la pregunta que te ocupa ahora. Tres caminos. Un mismo hogar.",
    authorImgAlt: "Phineas J. Cohen",

    closeTitle: "¿Por dónde te conviene empezar ahora?",
    closeBody:
      "Si quieres entender lo que te pasa a lo largo del tiempo, empieza por el viaje. Si una pregunta apremia ahora, ve hacia el informe. Si primero necesitas sentir que alguien ya caminó por algo parecido, empieza por el libro.",
    closeCtaFeel: "Empezar un viaje personal con InnerVoice",
    closeCtaQuestion: "Recibir un informe en profundidad sobre una pregunta que me importa",
    closeCtaBook: "Conocer el libro",

    footerBook: "Libro",
    footerApp: "InnerVoice",
    footerOrasis: "Orasis",
    footerPrivacy: "Privacidad",
    footerTerms: "Términos",
    footerContact: "Contacto",
    langHe: "עברית",
    langEn: "English",
    langEs: "Español",
  },
};

function detectLang() {
  const qs = new URLSearchParams(window.location.search);
  const urlLang = qs.get("lang");
  if (urlLang && HUB_COPY[urlLang]) return urlLang;
  const fromIv = window.localStorage.getItem("innervoice_lang");
  if (fromIv && HUB_COPY[fromIv]) return fromIv;
  const fromBooks = window.localStorage.getItem("books_landing_lang");
  if (fromBooks && HUB_COPY[fromBooks]) return fromBooks;
  const b = (navigator.language || "en").toLowerCase();
  if (b.startsWith("he")) return "he";
  if (b.startsWith("es")) return "es";
  return "en";
}

function withLang(path, lang) {
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}lang=${encodeURIComponent(lang)}`;
}

function HubLink({ href, className, children, onClick }) {
  const trackGate = () => {
    const h = String(href || "");
    const langHint =
      new URLSearchParams(window.location.search).get("lang") ||
      document.documentElement.lang ||
      "en";
    if (h.includes("/book")) trackBookClick("brand_hub_gate", { lang: langHint });
    else if (h.includes("innervoice")) trackInnerVoiceClick("brand_hub_gate", { lang: langHint });
    else if (h.includes("/orasis")) trackOrasisStagingClick("brand_hub_gate", { lang: langHint });
  };

  return (
    <a
      className={className}
      href={href}
      onClick={(e) => {
        trackGate();
        if (typeof onClick === "function") onClick(e);
      }}
    >
      {children}
    </a>
  );
}

export default function SiteHubUnifiedBalanced() {
  const [lang, setLang] = useState(detectLang);
  const [menuOpen, setMenuOpen] = useState(false);
  const t = useMemo(() => HUB_COPY[lang] || HUB_COPY.en, [lang]);

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.documentElement.dir = t.dir;
    try {
      window.localStorage.setItem("innervoice_lang", lang);
      window.localStorage.setItem("books_landing_lang", lang);
    } catch {
      /* ignore */
    }
  }, [lang, t]);

  useEffect(() => {
    return applyBrandHubSeo({ lang, pathBase: "/" });
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await bootAnalytics();
      if (!cancelled) trackPageView({ surface: "brand_hub", lang });
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const switchLang = (next) => {
    setLang(next);
    setMenuOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next);
    window.history.replaceState({}, "", url.toString());
  };

  const homePath = withLang("/", lang);
  const bookPath = withLang("/book", lang);
  const appPath = withLang("/innervoice", lang);
  const orasisPath = "/orasis";
  const privacyPath = withLang("/legal/privacy", lang);
  const termsPath = withLang("/legal/terms", lang);
  const contactHref = "mailto:support@notbrokenforever.com";
  const closeMenu = () => setMenuOpen(false);
  const bookCover = BOOK_COVER[lang] || BOOK_COVER.en;
  const ivPreview = IV_PREVIEW[lang] || IV_PREVIEW.en;
  const orasisThumb = ORASIS_THUMB[lang] || ORASIS_THUMB.en;

  return (
    <div className="nbf-hub nbf-hub--journey nbf-hub--balanced nbf-hub--entry" dir={t.dir}>
      <header className="nbf-hub__header">
        <HubLink className="nbf-hub__brand" href={homePath} onClick={closeMenu}>
          Not Broken Forever
        </HubLink>
        <nav className="nbf-hub__nav nbf-hub__nav--desktop" aria-label={t.navAria}>
          <HubLink href={homePath}>{t.navHome}</HubLink>
          <HubLink href={bookPath}>{t.navBook}</HubLink>
          <HubLink href={appPath}>{t.navInnerVoice}</HubLink>
          <HubLink href={orasisPath}>{t.navOrasis}</HubLink>
        </nav>
        <div className="nbf-hub__header-end">
          <div className="nbf-hub__lang" role="group" aria-label={t.langGroupAria}>
            {[
              { code: "he", label: t.langHe },
              { code: "en", label: t.langEn },
              { code: "es", label: t.langEs },
            ].map(({ code, label }) => (
              <button
                key={code}
                type="button"
                className={lang === code ? "nbf-hub__lang--on" : ""}
                onClick={() => switchLang(code)}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="nbf-hub__menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="nbf-hub-mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? t.navMenuClose : t.navMenuOpen}
          </button>
        </div>
      </header>

      {menuOpen ? (
        <nav id="nbf-hub-mobile-nav" className="nbf-hub__nav nbf-hub__nav--mobile" aria-label={t.navAria}>
          <HubLink href={homePath} onClick={closeMenu}>
            {t.navHome}
          </HubLink>
          <HubLink href={bookPath} onClick={closeMenu}>
            {t.navBook}
          </HubLink>
          <HubLink href={appPath} onClick={closeMenu}>
            {t.navInnerVoice}
          </HubLink>
          <HubLink href={orasisPath} onClick={closeMenu}>
            {t.navOrasis}
          </HubLink>
        </nav>
      ) : null}

      <main>
        <section className="nbf-hub__hero nbf-hub__hero--journey" aria-labelledby="nbf-hero-title">
          <div className="nbf-hub__hero-glow" aria-hidden="true" />
          <div className="nbf-hub__shell nbf-hub__hero-inner">
            <h1 id="nbf-hero-title" className="nbf-hub__hero-title nbf-hub__hero-title--wide">
              {t.heroTitle}
            </h1>
            <p className="nbf-hub__hero-boom">{t.heroBoom}</p>
            <p className="nbf-hub__hero-lead">{t.heroBody}</p>
            <div className="nbf-hub__hero-actions nbf-hub__hero-actions--stack">
              <HubLink className="nbf-hub__btn nbf-hub__btn--primary" href={appPath}>
                {t.heroCtaFeel}
              </HubLink>
              <HubLink className="nbf-hub__btn nbf-hub__btn--ghost" href={orasisPath}>
                {t.heroCtaQuestion}
              </HubLink>
              <HubLink className="nbf-hub__btn nbf-hub__btn--ghost" href={bookPath}>
                {t.heroCtaBook}
              </HubLink>
            </div>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--recog" aria-labelledby="nbf-recog-title">
          <div className="nbf-hub__shell nbf-hub__narrow">
            <h2 id="nbf-recog-title" className="nbf-hub__section-title">
              {t.recogTitle}
            </h2>
            <p className="nbf-hub__section-text">{t.recogBody}</p>
            <ul className="nbf-hub__recog-list">
              {t.recogItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--prose" aria-labelledby="nbf-brand-title">
          <div className="nbf-hub__shell nbf-hub__narrow">
            <h2 id="nbf-brand-title" className="nbf-hub__section-title">
              {t.brandTitle}
            </h2>
            <p className="nbf-hub__section-text">{t.brandBody}</p>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--equal-gates" aria-label={t.gatesLabel}>
          <div className="nbf-hub__shell">
            <div className="nbf-hub__equal-grid">
              <article className="nbf-hub__equal-card" id="gate-book">
                <figure className="nbf-hub__equal-media nbf-hub__equal-media--book">
                  <img src={bookCover} alt={t.bookImgAlt} width="320" height="480" loading="eager" />
                </figure>
                <p className="nbf-hub__path-kicker">{t.bookLabel}</p>
                <h3 className="nbf-hub__path-title">{t.bookTitle}</h3>
                <p className="nbf-hub__equal-promise">{t.bookPromise}</p>
                <ul className="nbf-hub__equal-points">
                  {t.bookPoints.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={bookPath}>
                  {t.bookCta}
                </HubLink>
              </article>

              <article className="nbf-hub__equal-card" id="gate-innervoice">
                <figure className="nbf-hub__equal-media nbf-hub__equal-media--iv">
                  <img
                    src={ivPreview}
                    alt={t.ivImgAlt}
                    width="390"
                    height="844"
                    loading="eager"
                  />
                </figure>
                <p className="nbf-hub__path-kicker">{t.ivLabel}</p>
                <h3 className="nbf-hub__path-title">{t.ivTitle}</h3>
                <p className="nbf-hub__equal-promise">{t.ivPromise}</p>
                <ul className="nbf-hub__equal-points">
                  {t.ivPoints.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={appPath}>
                  {t.ivCta}
                </HubLink>
              </article>

              <article className="nbf-hub__equal-card" id="gate-orasis">
                <figure className="nbf-hub__equal-media nbf-hub__equal-media--orasis">
                  <img
                    src={orasisThumb}
                    alt={t.orasisImgAlt}
                    width="420"
                    height="525"
                    loading="eager"
                  />
                </figure>
                <p className="nbf-hub__path-kicker">{t.orasisLabel}</p>
                <h3 className="nbf-hub__path-title">{t.orasisTitle}</h3>
                <p className="nbf-hub__equal-promise">{t.orasisPromise}</p>
                <p className="nbf-hub__equal-brief-label">{t.orasisPointsIntro}</p>
                <ul className="nbf-hub__equal-brief">
                  {ORASIS_BRIEF.map((item) => (
                    <li key={item.id}>
                      <span>{item.title[lang] || item.title.en}</span>
                      <strong>{item.price}</strong>
                    </li>
                  ))}
                </ul>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={orasisPath}>
                  {t.orasisCta}
                </HubLink>
              </article>
            </div>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--compare" aria-labelledby="nbf-compare-title">
          <div className="nbf-hub__shell">
            <div className="nbf-hub__narrow">
              <h2 id="nbf-compare-title" className="nbf-hub__section-title">
                {t.compareTitle}
              </h2>
              <p className="nbf-hub__section-text">{t.compareBody}</p>
            </div>
            <div className="nbf-hub__compare-grid">
              <article className="nbf-hub__compare-card">
                <h3>{t.bookLabel}</h3>
                <p>{t.compareBookWhen}</p>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={bookPath}>
                  {t.bookCta}
                </HubLink>
              </article>
              <article className="nbf-hub__compare-card">
                <h3>{t.ivLabel}</h3>
                <p>{t.compareIvWhen}</p>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={appPath}>
                  {t.ivCta}
                </HubLink>
              </article>
              <article className="nbf-hub__compare-card">
                <h3>{t.orasisLabel}</h3>
                <p>{t.compareOrasisWhen}</p>
                <HubLink className="nbf-hub__btn nbf-hub__btn--card" href={orasisPath}>
                  {t.orasisCta}
                </HubLink>
              </article>
            </div>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--author nbf-hub__section--author-strong" aria-labelledby="nbf-author-name">
          <div className="nbf-hub__shell nbf-hub__author-strong">
            <img
              className="nbf-hub__author-photo"
              src="/author/author-photo.jpg"
              alt={t.authorImgAlt}
              width="160"
              height="160"
              loading="lazy"
            />
            <div>
              <p className="nbf-hub__author-eyebrow">{t.authorEyebrow}</p>
              <h2 id="nbf-author-name" className="nbf-hub__author-name">
                {t.authorName}
              </h2>
              <p className="nbf-hub__author-text">{t.authorBody}</p>
            </div>
          </div>
        </section>

        <section className="nbf-hub__section nbf-hub__section--close" aria-labelledby="nbf-close-title">
          <div className="nbf-hub__shell nbf-hub__narrow">
            <h2 id="nbf-close-title" className="nbf-hub__section-title">
              {t.closeTitle}
            </h2>
            <p className="nbf-hub__section-text">{t.closeBody}</p>
            <div className="nbf-hub__hero-actions nbf-hub__hero-actions--stack">
              <HubLink className="nbf-hub__btn nbf-hub__btn--primary" href={appPath}>
                {t.closeCtaFeel}
              </HubLink>
              <HubLink className="nbf-hub__btn nbf-hub__btn--ghost" href={orasisPath}>
                {t.closeCtaQuestion}
              </HubLink>
              <HubLink className="nbf-hub__btn nbf-hub__btn--ghost" href={bookPath}>
                {t.closeCtaBook}
              </HubLink>
            </div>
          </div>
        </section>
      </main>

      <footer className="nbf-hub__footer">
        <div className="nbf-hub__shell nbf-hub__footer-inner">
          <nav className="nbf-hub__footer-nav" aria-label="Footer">
            <HubLink href={bookPath}>{t.footerBook}</HubLink>
            <HubLink href={appPath}>{t.footerApp}</HubLink>
            <HubLink href={orasisPath}>{t.footerOrasis}</HubLink>
            <HubLink href={privacyPath}>{t.footerPrivacy}</HubLink>
            <HubLink href={termsPath}>{t.footerTerms}</HubLink>
            <HubLink href={contactHref}>{t.footerContact}</HubLink>
          </nav>
        </div>
      </footer>
    </div>
  );
}
